// authEvents.ts — lightweight sign-in event bus.
//
// Replaces the duplicated `useAuthStore.subscribe((state, prev) => { … })`
// ladder that existed in 6+ stores. All callers register via `onSignIn()`
// instead; this module owns one shared subscription so HMR doesn't
// accumulate a duplicate listener per file save.
//
// Usage:
//   import { onSignIn } from '../lib/authEvents';
//   onSignIn(() => void useMyStore.getState().syncFromServer());
//
// Existing `useAuthStore.subscribe(...)` calls in individual stores
// can be migrated here incrementally — the new pattern is strictly
// additive and safe to run alongside the old calls during migration.

import { useAuthStore } from '../stores/authStore';

/** All registered sign-in handlers. */
const handlers: Array<() => void> = [];

/** Module-level Zustand subscription handle.
 *  Stored so HMR can tear down the previous listener before re-attaching
 *  (Vite re-executes modules on save, resetting module-level state). */
let unsub: (() => void) | null = null;

function attach(): void {
  unsub?.();
  unsub = useAuthStore.subscribe((state, prev) => {
    if (!state.user) return;
    // Only fire on transitions from no-user → user.
    if (prev.user?.id === state.user.id) return;
    for (const h of handlers) h();
  });
}

attach();

// HMR cleanup — if Vite re-executes this module, tear down the previous
// subscription before `attach()` in the new module instance fires.
if (import.meta.hot) {
  import.meta.hot.dispose(() => unsub?.());
}

/**
 * Register a callback that fires on every sign-in transition
 * (signed-out → signed-in). If the user is already signed in at the
 * time of registration, the callback fires immediately (best-effort
 * initial hydration).
 *
 * @returns An unregister function. Call it in a store's cleanup if needed.
 */
export function onSignIn(handler: () => void): () => void {
  if (!handlers.includes(handler)) handlers.push(handler);
  // If already signed in, fire immediately so a late registration
  // (e.g. after an async import) doesn't miss the current session.
  if (useAuthStore.getState().user) {
    try { handler(); } catch { /* don't let a bad handler break registration */ }
  }
  return () => {
    const idx = handlers.indexOf(handler);
    if (idx >= 0) handlers.splice(idx, 1);
  };
}
